import { Image as KonvaImage, Layer, Stage } from 'react-konva';
import type { Frame } from '~/types/pipeline';
import type { NormalizedPreviewCanvas } from './normalizedCanvas';

type Props = {
  frame: Frame;
  bitmap: ImageBitmap;
  size: number;
  /** Shared preview canvas (size + origin shift). */
  canvas: NormalizedPreviewCanvas;
  /** Single-frame ghost overlay (previous frame). Ignored when `stack` is set. */
  ghost?: Frame | null;
  /** All frames to render stacked at low opacity. Pixel-perfect alignment aid. */
  stack?: readonly Frame[];
};

/** Square Konva preview of a sprite-sheet cell, normalized to a shared canvas. */
export function FramePreview({ frame, bitmap, size, canvas, ghost, stack }: Props) {
  const denom = Math.max(canvas.w, canvas.h, 1);
  const scale = size / denom;

  if (stack) {
    const alpha = Math.min(0.6, 1 / Math.sqrt(Math.max(1, stack.length)));
    return (
      <Stage width={size} height={size}>
        <Layer listening={false}>
          {stack.map((f) => (
            <CellImage
              key={f.index}
              frame={f}
              bitmap={bitmap}
              scale={scale}
              canvas={canvas}
              opacity={alpha}
            />
          ))}
        </Layer>
      </Stage>
    );
  }

  return (
    <Stage width={size} height={size}>
      <Layer listening={false}>
        {ghost && ghost !== frame && (
          <CellImage frame={ghost} bitmap={bitmap} scale={scale} canvas={canvas} opacity={0.3} />
        )}
        <CellImage frame={frame} bitmap={bitmap} scale={scale} canvas={canvas} />
      </Layer>
    </Stage>
  );
}

function CellImage({
  frame,
  bitmap,
  scale,
  canvas,
  opacity = 1,
}: {
  frame: Frame;
  bitmap: ImageBitmap;
  scale: number;
  canvas: NormalizedPreviewCanvas;
  opacity?: number;
}) {
  const { trimRect, spriteSourceSize } = frame;
  const x = (canvas.dx + spriteSourceSize.x) * scale;
  const y = (canvas.dy + spriteSourceSize.y) * scale;
  return (
    <KonvaImage
      image={bitmap}
      x={x}
      y={y}
      width={trimRect.w * scale}
      height={trimRect.h * scale}
      crop={{ x: trimRect.x, y: trimRect.y, width: trimRect.w, height: trimRect.h }}
      opacity={opacity}
    />
  );
}
